import { YAxisOptions } from 'highcharts/highcharts';
import { WidgetType } from 'types/widget.types';
import { parseFiniteNumberWithNaN } from 'utils/number';

import { DEFAULT_TEXT_STYLE, DEFAULT_Y2_Label_TOGGLE } from '../ChartWidget.config';
import { CustomLegendRender, formatDataLabel } from '../ChartWidget.helpers';
import { HighchartsSeriesData } from '../ChartWidget.hooks';
import { ControlledHeader } from '../ChartWidget.types';
import { HIDDEN_AXIS_LINE_COLOR, HIDDEN_AXIS_LINE_WIDTH } from '../DataTableChartCommon/DataTableChartCommon.config';
import { HighchartsOptions } from '../DataTableChartCommon/DataTableChartCommon.types';
import {
  DEFAULT_COMBO_CHART_DATA,
  DEFAULT_ENABLE_Y2_AXIS_LINE,
  DEFAULT_HEIGHT,
  DEFAULT_WIDTH,
} from './ComboChartWidget.config';
import { ComboChartWidgetData } from './ComboChartWidget.types';
import { VERSION } from './ComboChartWidget.upgrade';

export const generateDefaultData = () => {
  return {
    widgetType: WidgetType.ComboChart,
    widgetData: {
      version: VERSION,
      widthPx: DEFAULT_WIDTH,
      heightPx: DEFAULT_HEIGHT,
      topPx: 0,
      leftPx: 0,
      rotateDeg: 0,
      altText: '',
      isDecorative: false,
      patterns: {
        enabled: false,
        list: [],
      },
      // Combo chart specific data
      ...DEFAULT_COMBO_CHART_DATA,
    } as ComboChartWidgetData,
  };
};

export const getHighChartsComboConfig = (
  defaultOptions: HighchartsOptions,
  widgetData: ComboChartWidgetData,
  categories: Array<string>,
  series: HighchartsSeriesData[],
  keyCustom: number,
) => {
  const options = defaultOptions;

  options.chart = options.chart || {};
  options.chart.events = { render: CustomLegendRender };

  options.keyCustom = keyCustom;

  options.series = series.map(({ controlledHeader, ...otherSeriesData }) => {
    const yAxis = controlledHeader === ControlledHeader.ComboColumn ? 0 : 1;

    if (controlledHeader === ControlledHeader.ComboColumn) {
      return {
        ...otherSeriesData,
        dataLabels: {
          style: {
            ...DEFAULT_TEXT_STYLE,
            ...widgetData.dataLabels.style,
          },
        },
        visible: true,
        type: 'column', // 'type' is required explicitly before compiling to pass the type check.
        xAxis: 0,
        yAxis,
        zIndex: 1,
      };
    } else {
      return {
        ...otherSeriesData,
        dataLabels: {
          formatter: function () {
            return formatDataLabel({
              dataLabel: this.y,
              prefix: widgetData.dataLabels.y2Prefix,
              suffix: widgetData.dataLabels.y2Suffix,
              precision: widgetData.numberFormat?.y2Precision,
              separator: widgetData.numberFormat?.y2Separator,
            });
          },
          enabled: widgetData.y2DataLabels?.enabled ?? DEFAULT_Y2_Label_TOGGLE,
          style: {
            ...DEFAULT_TEXT_STYLE,
            ...widgetData.y2DataLabels?.style,
          },
        },
        visible: true,
        type: 'line', // 'type' is required explicitly before compiling to pass the type check.
        xAxis: 0,
        yAxis,
        zIndex: 2,
      };
    }
  });

  options.xAxis = {
    ...((options.xAxis as Highcharts.XAxisOptions) || {}),
    categories,
  };

  const enableY2Line = widgetData.y2Axis?.enableLine ?? DEFAULT_ENABLE_Y2_AXIS_LINE;
  const minY2Value = parseFiniteNumberWithNaN(widgetData.seriesMeta?.minY2Value);
  const maxY2Value = parseFiniteNumberWithNaN(widgetData.seriesMeta?.maxY2Value);
  options.yAxis = [
    defaultOptions.yAxis as YAxisOptions,
    {
      ...defaultOptions.yAxis,
      // We use the x-axis line color and width for all axis settings.
      lineColor: enableY2Line ? widgetData.xAxis.style.lineColor : HIDDEN_AXIS_LINE_COLOR,
      lineWidth: enableY2Line ? widgetData.xAxis.style.lineWidth : HIDDEN_AXIS_LINE_WIDTH,
      max: Number.isNaN(maxY2Value) ? null : maxY2Value,
      min: Number.isNaN(minY2Value) ? null : minY2Value,
      opposite: true,
      title: {
        ...(defaultOptions.yAxis as YAxisOptions)?.title,
        text: widgetData.y2Axis?.title.text ?? '',
      },
    },
  ];

  return options;
};
